import type { ErrorInfo, ReactNode } from 'react'
import { Component } from 'react'
import { logError } from '@/components/shared/error/logError'
import { Link } from '@/components/shared/links/Link'
import { buttonStylesSecondary } from '@/components/shared/links/styles'
import { isDev } from '@/components/shared/utils/isEnv'

type ErrorBoundaryState = { hasError: boolean; error: Error | null }

type ErrorBoundaryProps = {
  children: ReactNode
  fallback: (props: { error: Error; reset: () => void }) => ReactNode
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logError(error, { componentStack: errorInfo.componentStack ?? undefined })
  }

  reset = () => this.setState({ hasError: false, error: null })

  override render() {
    if (this.state.hasError && this.state.error) {
      return this.props.fallback({ error: this.state.error, reset: this.reset })
    }
    return this.props.children
  }
}

export function RootErrorFallback({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <div className="not-prose flex min-h-full grow flex-col bg-transparent">
      <main className="mx-auto flex w-full max-w-7xl grow flex-col justify-center px-4 sm:px-6 lg:px-8">
        <div className="py-16">
          <div className="text-center">
            <p className="text-base font-semibold text-amber-500">:-(</p>
            <h1 className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              Ein Fehler ist aufgetreten
            </h1>
            <p className="mt-2 text-base text-gray-500">Leider ist ein Fehler aufgetreten.</p>
            {isDev && error?.message && (
              <p className="mt-2 font-mono text-sm text-gray-600" role="alert">
                {error.message}
              </p>
            )}
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Link button to="/">
                Zur Startseite
              </Link>
              <button type="button" onClick={reset} className={buttonStylesSecondary}>
                Erneut versuchen
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
